/**
 * HUG - Artist Management Platform
 * Copyright (c) 2025 Underplay Music Ltd. All rights reserved.
 * 
 * This software and associated documentation files are the proprietary 
 * property of Underplay Music Ltd and are protected by copyright law.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { UndoProvider } from './contexts/UndoContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Tasks from './pages/Tasks';
import LiveShows from './pages/LiveShows';
import Files from './pages/Files';
import Diary from './pages/Diary';
import Social from './pages/Social';
import Invoices from './pages/Invoices';
import News from './pages/News';
import Settings from './pages/Settings';
import ArtistDetails from './pages/ArtistDetails';
import ArtistManagement from './pages/ArtistManagement';
import Finance from './pages/Finance';
import FinanceOverview from './pages/FinanceOverview';
import Highlights from './pages/Highlights';
import RecordLabel from './pages/RecordLabel';
import UserManagement from './pages/UserManagement';
import AcceptInvite from './pages/AcceptInvite';
import AdminRoyalties from './pages/AdminRoyalties';
import ArtistPortalEarnings from './pages/ArtistPortalEarnings';
import Paythroughs from './pages/Paythroughs';
import Layout from './components/Layout';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedArtist, setSelectedArtist] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setIsAuthenticated(true);
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      if (parsedUser.role === 'artist') {
        setSelectedArtist(parsedUser.id);
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
    if (userData.role === 'artist') {
      setSelectedArtist(userData.id);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
    setSelectedArtist(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <UndoProvider>
        <BrowserRouter>
          <Routes>
          {/* Public routes */}
          <Route
            path="/accept-invite"
            element={<AcceptInvite setUser={handleLogin} />}
          />
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <Dashboard user={user} selectedArtist={selectedArtist} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/tasks"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <Tasks user={user} selectedArtist={selectedArtist} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/live-shows"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <LiveShows user={user} selectedArtist={selectedArtist} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/files"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <Files user={user} selectedArtist={selectedArtist} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/diary"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <Diary user={user} selectedArtist={selectedArtist} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/social"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <Social user={user} selectedArtist={selectedArtist} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/invoices"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <Invoices user={user} selectedArtist={selectedArtist} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/news"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <News user={user} selectedArtist={selectedArtist} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/settings"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <Settings user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/artist-details"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <ArtistDetails user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/artists"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <ArtistManagement user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/finance"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <Finance user={user} selectedArtist={selectedArtist} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/finance-overview"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <FinanceOverview user={user} setSelectedArtist={setSelectedArtist} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/highlights"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <Highlights user={user} selectedArtist={selectedArtist} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/record-label"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <RecordLabel user={user} selectedArtist={selectedArtist} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/user-management"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <UserManagement user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/royalties"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <AdminRoyalties user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/my-earnings"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <ArtistPortalEarnings user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/paythroughs"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} selectedArtist={selectedArtist} setSelectedArtist={setSelectedArtist}>
                  <Paythroughs user={user} selectedArtist={selectedArtist} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </UndoProvider>
    </>
  );
}

export default App;